import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { PaDrawer } from "components/platform-admin";
import {
  createPlatformStaffUser,
  updatePlatformStaffUser,
} from "store/services/platformAdmin.services";
import {
  actorIsPlatformOwner,
  LAST_OWNER_GUARD_MESSAGE,
  wouldRemoveLastOwner,
} from "./staffUsers.utils";

const createDefaults = {
  name: "",
  email: "",
  delivery: "invite",
  password: "",
  role_ids: [],
};

const StaffUserDrawer = ({
  open,
  onClose,
  mode,
  staffUser,
  roleOptions,
  currentUser,
  onSaved,
}) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(createDefaults);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(createDefaults);
      setFormError("");
      return;
    }

    if (isEdit && staffUser) {
      setForm({
        name: staffUser.name || "",
        email: staffUser.email || "",
        delivery: "invite",
        password: "",
        role_ids: (staffUser.platform_roles || []).map((role) => role.id),
      });
      return;
    }

    setForm(createDefaults);
  }, [open, isEdit, staffUser]);

  const ownerRoleIds = useMemo(
    () => roleOptions.filter((role) => role.is_platform_owner).map((role) => role.id),
    [roleOptions]
  );

  const assignableRoleOptions = useMemo(() => {
    if (actorIsPlatformOwner(currentUser)) {
      return roleOptions;
    }

    return roleOptions.filter((role) => !role.is_platform_owner);
  }, [currentUser, roleOptions]);

  const lastOwnerLocked = Boolean(isEdit && staffUser?.is_last_platform_owner);

  const toggleRole = (roleId) => {
    setForm((prev) => {
      const selected = prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((id) => id !== roleId)
        : [...prev.role_ids, roleId];

      if (
        lastOwnerLocked &&
        ownerRoleIds.length > 0 &&
        !selected.some((id) => ownerRoleIds.includes(id))
      ) {
        return prev;
      }

      return { ...prev, role_ids: selected };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!form.name.trim()) {
      setFormError("Name is required.");
      return;
    }

    if (!isEdit && !form.email.trim()) {
      setFormError("Email is required.");
      return;
    }

    if (form.role_ids.length === 0) {
      setFormError("Select at least one platform role.");
      return;
    }

    if (
      isEdit &&
      wouldRemoveLastOwner(staffUser, form.role_ids, roleOptions)
    ) {
      setFormError(LAST_OWNER_GUARD_MESSAGE);
      return;
    }

    if (
      !isEdit &&
      form.delivery === "password" &&
      form.password.length < 8
    ) {
      setFormError("Password must be at least 8 characters.");
      return;
    }

    if (isEdit && form.password && form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);

    try {
      let savedUser = null;

      if (isEdit) {
        savedUser = await updatePlatformStaffUser(staffUser.id, {
          name: form.name.trim(),
          role_ids: form.role_ids,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success("Staff user updated");
      } else {
        savedUser = await createPlatformStaffUser({
          name: form.name.trim(),
          email: form.email.trim(),
          role_ids: form.role_ids,
          delivery: form.delivery,
          password: form.delivery === "password" ? form.password : undefined,
        });
        toast.success("Staff user created");
      }

      onSaved(savedUser);
      onClose();
    } catch (error) {
      toast.error(error?.message || "Failed to save staff user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PaDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit platform staff" : "Add platform staff"}
      description={
        isEdit
          ? "Update staff details and platform roles."
          : "Create a platform staff account and assign roles."
      }
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="staff-user-form"
            disabled={submitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create staff"}
          </button>
        </div>
      }
    >
      <form id="staff-user-form" className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            value={form.email}
            readOnly={isEdit}
            onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm ${
              isEdit ? "border-gray-200 bg-gray-50 text-gray-700" : "border-gray-300"
            }`}
          />
        </div>

        {!isEdit ? (
          <>
            <div>
              <span className="block text-sm font-medium text-gray-700">Delivery</span>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="delivery"
                    value="invite"
                    checked={form.delivery === "invite"}
                    onChange={() => setForm((prev) => ({ ...prev, delivery: "invite" }))}
                  />
                  Send Cognito invite
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="delivery"
                    value="password"
                    checked={form.delivery === "password"}
                    onChange={() => setForm((prev) => ({ ...prev, delivery: "password" }))}
                  />
                  Set password now
                </label>
              </div>
            </div>

            {form.delivery === "password" ? (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Temporary password
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            ) : null}
          </>
        ) : null}

        {isEdit ? (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              New password
            </label>
            <p className="mt-0.5 text-xs text-gray-500">
              Optional. Set a new login password in Cognito if the user cannot sign in.
            </p>
            <input
              type="password"
              value={form.password}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, password: event.target.value }))
              }
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        ) : null}

        <div>
          <div className="flex items-center justify-between gap-3">
            <span className="block text-sm font-medium text-gray-700">Platform roles</span>
            {lastOwnerLocked ? (
              <span className="text-xs text-amber-700">Last Owner role is locked</span>
            ) : null}
          </div>
          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
            {assignableRoleOptions.length === 0 ? (
              <p className="text-sm text-gray-500">No platform roles available.</p>
            ) : (
              assignableRoleOptions.map((role) => {
                const checked = form.role_ids.includes(role.id);
                const lockedOwner =
                  lastOwnerLocked && role.is_platform_owner && checked;

                return (
                  <label
                    key={role.id}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={lockedOwner}
                      title={lockedOwner ? LAST_OWNER_GUARD_MESSAGE : undefined}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span>
                      {role.name}
                      {role.is_platform_owner ? (
                        <span className="ml-2 text-xs text-indigo-600">Owner</span>
                      ) : null}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {formError ? <p className="text-sm text-red-600">{formError}</p> : null}
      </form>
    </PaDrawer>
  );
};

export default StaffUserDrawer;
